// Fractions & Percent Calculators helpers: simplifying, converting (to/from
// decimal and percent), the four basic operations on fractions, mixed
// number/improper fraction conversion, comparing fractions, and finding a
// fraction of a number. Percentage-specific tools (percent change, discount,
// tip, etc.) already live in lib/tools/percentRatioCalculators.ts under the
// separate Percent & Ratio Calculators category - this file is specifically
// fraction arithmetic and fraction<->decimal/percent conversion.
import { t } from './i18n'

function gcdTwo(a: number, b: number): number {
    a = Math.abs(a)
    b = Math.abs(b)
    return b === 0 ? a : gcdTwo(b, a % b)
}
function lcmTwo(a: number, b: number): number {
    return Math.abs(a * b) / gcdTwo(a, b)
}
// Reduces a fraction to lowest terms and normalizes the sign onto the
// numerator, so the denominator is always shown as positive
function simplifyFraction(num: number, denom: number): { num: number; denom: number } {
    if (denom < 0) {
        num = -num
        denom = -denom
    }
    const g = gcdTwo(num, denom) || 1
    return { num: num / g, denom: denom / g }
}
// Converts a decimal to an exact fraction by reading its printed decimal
// places (e.g. 0.75 -> 75/100), then simplifying - works for any terminating
// decimal, which covers all inputs a number field can actually produce
function decimalToFractionParts(decimal: number): { num: number; denom: number } {
    const str = String(decimal)
    const dotIndex = str.indexOf('.')
    if (dotIndex === -1) return simplifyFraction(Number(str), 1)
    const decimalPlaces = str.length - dotIndex - 1
    const denom = Math.pow(10, decimalPlaces)
    const num = Math.round(decimal * denom)
    return simplifyFraction(num, denom)
}

// ============================================================
// 1. Fraction Simplifier
// ============================================================
export function fractionSimplifier(params: { numerator: unknown; denominator: unknown }): {
    simplified_numerator: number; simplified_denominator: number; simplified_fraction: string
} {
    const num = Math.round(Number(params.numerator)) || 0
    const denom = Math.round(Number(params.denominator)) || 1
    const { num: n, denom: d } = simplifyFraction(num, denom)
    return { simplified_numerator: n, simplified_denominator: d, simplified_fraction: `${n}/${d}` }
}

// ============================================================
// 2. Fraction to Decimal Calculator
// ============================================================
export function fractionToDecimalCalculator(params: { numerator: unknown; denominator: unknown }): { decimal_result: number } {
    const num = Number(params.numerator) || 0
    const denom = Number(params.denominator) || 1
    return { decimal_result: num / denom }
}

// ============================================================
// 3. Decimal to Fraction Calculator
// ============================================================
export function decimalToFractionCalculator(params: { decimal: unknown }): {
    numerator: number; denominator: number; fraction: string
} {
    const decimal = Number(params.decimal) || 0
    const { num, denom } = decimalToFractionParts(decimal)
    return { numerator: num, denominator: denom, fraction: `${num}/${denom}` }
}

// ============================================================
// 4. Fraction to Percent Calculator
// ============================================================
export function fractionToPercentCalculator(params: { numerator: unknown; denominator: unknown }): { percent: number } {
    const num = Number(params.numerator) || 0
    const denom = Number(params.denominator) || 1
    return { percent: (num / denom) * 100 }
}

// ============================================================
// 5. Percent to Fraction Calculator
// ============================================================
export function percentToFractionCalculator(params: { percent: unknown }): {
    numerator: number; denominator: number; fraction: string
} {
    const percent = Number(params.percent) || 0
    const { num, denom } = decimalToFractionParts(percent / 100)
    return { numerator: num, denominator: denom, fraction: `${num}/${denom}` }
}

// ============================================================
// 6. Adding Fractions Calculator
// ============================================================
export function addingFractionsCalculator(params: {
    numerator1: unknown; denominator1: unknown; numerator2: unknown; denominator2: unknown
}): { result_numerator: number; result_denominator: number; result_fraction: string; decimal_result: number } {
    const n1 = Math.round(Number(params.numerator1)) || 0
    const d1 = Math.round(Number(params.denominator1)) || 1
    const n2 = Math.round(Number(params.numerator2)) || 0
    const d2 = Math.round(Number(params.denominator2)) || 1
    const commonDenom = lcmTwo(d1, d2)
    const num = n1 * (commonDenom / d1) + n2 * (commonDenom / d2)
    const { num: rn, denom: rd } = simplifyFraction(num, commonDenom)
    return { result_numerator: rn, result_denominator: rd, result_fraction: `${rn}/${rd}`, decimal_result: rn / rd }
}

// ============================================================
// 7. Subtracting Fractions Calculator
// ============================================================
export function subtractingFractionsCalculator(params: {
    numerator1: unknown; denominator1: unknown; numerator2: unknown; denominator2: unknown
}): { result_numerator: number; result_denominator: number; result_fraction: string; decimal_result: number } {
    const n1 = Math.round(Number(params.numerator1)) || 0
    const d1 = Math.round(Number(params.denominator1)) || 1
    const n2 = Math.round(Number(params.numerator2)) || 0
    const d2 = Math.round(Number(params.denominator2)) || 1
    const commonDenom = lcmTwo(d1, d2)
    const num = n1 * (commonDenom / d1) - n2 * (commonDenom / d2)
    const { num: rn, denom: rd } = simplifyFraction(num, commonDenom)
    return { result_numerator: rn, result_denominator: rd, result_fraction: `${rn}/${rd}`, decimal_result: rn / rd }
}

// ============================================================
// 8. Multiplying Fractions Calculator
// ============================================================
export function multiplyingFractionsCalculator(params: {
    numerator1: unknown; denominator1: unknown; numerator2: unknown; denominator2: unknown
}): { result_numerator: number; result_denominator: number; result_fraction: string; decimal_result: number } {
    const n1 = Math.round(Number(params.numerator1)) || 0
    const d1 = Math.round(Number(params.denominator1)) || 1
    const n2 = Math.round(Number(params.numerator2)) || 0
    const d2 = Math.round(Number(params.denominator2)) || 1
    const { num: rn, denom: rd } = simplifyFraction(n1 * n2, d1 * d2)
    return { result_numerator: rn, result_denominator: rd, result_fraction: `${rn}/${rd}`, decimal_result: rn / rd }
}

// ============================================================
// 9. Dividing Fractions Calculator
// ============================================================
export function dividingFractionsCalculator(params: {
    numerator1: unknown; denominator1: unknown; numerator2: unknown; denominator2: unknown
}): { result_numerator: number | string; result_denominator: number | string; result_fraction: string; decimal_result: number | string } {
    const n1 = Math.round(Number(params.numerator1)) || 0
    const d1 = Math.round(Number(params.denominator1)) || 1
    const n2 = Math.round(Number(params.numerator2)) || 0
    const d2 = Math.round(Number(params.denominator2)) || 1
    if (n2 === 0) {
        return { result_numerator: '—', result_denominator: '—', result_fraction: 'undefined (division by zero)', decimal_result: '—' }
    }
    const { num: rn, denom: rd } = simplifyFraction(n1 * d2, d1 * n2)
    return { result_numerator: rn, result_denominator: rd, result_fraction: `${rn}/${rd}`, decimal_result: rn / rd }
}

// ============================================================
// 10. Mixed Number <-> Improper Fraction Converter
// ============================================================
export function mixedImproperConverter(params: {
    mode: unknown; whole_number: unknown; numerator: unknown; denominator: unknown
}): { result_numerator: number; result_denominator: number; whole_number_result: number; result_fraction: string } {
    const mode = String(params.mode || 'to_improper')
    const denom = Math.round(Number(params.denominator)) || 1

    if (mode === 'to_mixed') {
        const num = Math.round(Number(params.numerator)) || 0
        const whole = Math.trunc(num / denom)
        const remainder = Math.abs(num - whole * denom)
        return {
            result_numerator: remainder,
            result_denominator: denom,
            whole_number_result: whole,
            result_fraction: remainder === 0 ? `${whole}` : `${whole} ${remainder}/${denom}`,
        }
    }

    const whole = Math.round(Number(params.whole_number)) || 0
    const num = Math.round(Number(params.numerator)) || 0
    const sign = whole < 0 ? -1 : 1
    const improperNum = sign * (Math.abs(whole) * denom + num)
    return {
        result_numerator: improperNum,
        result_denominator: denom,
        whole_number_result: 0,
        result_fraction: `${improperNum}/${denom}`,
    }
}

// ============================================================
// 11. Comparing Fractions Calculator
// ============================================================
const GREATER_THAN: Record<string, string> = {
    en: 'Greater Than', ru: 'Больше чем', de: 'Größer Als', es: 'Mayor Que', fr: 'Supérieur À', it: 'Maggiore Di', pl: 'Większe Niż', lv: 'Lielāks Par',
}
const LESS_THAN: Record<string, string> = {
    en: 'Less Than', ru: 'Меньше чем', de: 'Kleiner Als', es: 'Menor Que', fr: 'Inférieur À', it: 'Minore Di', pl: 'Mniejsze Niż', lv: 'Mazāks Par',
}
const EQUAL_TO: Record<string, string> = {
    en: 'Equal To', ru: 'Равно', de: 'Gleich', es: 'Igual A', fr: 'Égal À', it: 'Uguale A', pl: 'Równe', lv: 'Vienāds Ar',
}
export function comparingFractionsCalculator(params: {
    numerator1: unknown; denominator1: unknown; numerator2: unknown; denominator2: unknown; language: unknown
}): { comparison_result: string; decimal1: number; decimal2: number } {
    const n1 = Number(params.numerator1) || 0
    const d1 = Number(params.denominator1) || 1
    const n2 = Number(params.numerator2) || 0
    const d2 = Number(params.denominator2) || 1
    const left = n1 * d2
    const right = n2 * d1
    let bucket: Record<string, string>
    if (left > right) bucket = GREATER_THAN
    else if (left < right) bucket = LESS_THAN
    else bucket = EQUAL_TO
    return { comparison_result: t(bucket, params.language), decimal1: n1 / d1, decimal2: n2 / d2 }
}

// ============================================================
// 12. Fraction of a Number Calculator
// ============================================================
export function fractionOfNumberCalculator(params: { numerator: unknown; denominator: unknown; number: unknown }): { result: number } {
    const num = Number(params.numerator) || 0
    const denom = Number(params.denominator) || 1
    const value = Number(params.number) || 0
    return { result: (num / denom) * value }
}
